import { useState, useCallback, useEffect } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import { NewReleasesOutlined, SearchOutlined } from "@mui/icons-material";
import NewDamageClaimDialog from "./NewDamageClaimDialog.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import PropTypes from "prop-types";
import { SEARCH_WORD_KEY } from "./tables/utils/tableUtils.js";
import ClaimStatusBadge from "./ClaimStatusBadge.jsx";
import { typographyTokens } from "@accredo/fe-shared/theme/index.js";

/**
 * ClaimsSearchBar Component
 * Reusable search bar for claims with autocomplete and create button
 */
export default function ClaimsSearchBar({ sx }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Debounced search function
  const debouncedSearch = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    setIsSearchLoading(true);
    setSearchError(null);

    try {
      const results = await backendGet(`data/schadens`, {
        label: "Schadensuche",
        setResult: (res) => {
          setSearchResults(res?.data || []);
        },
        options: {
          params: {
            filter: {
              [`${SEARCH_WORD_KEY}`]: query,
            },
            include: ["objekt"],
            page: 1,
            limit: 100,
          },
        },
      });

      if (results === null) {
        setSearchError("Fehler bei der Suche");
      }
    } catch (error) {
      setSearchError("Fehler bei der Suche");
      console.error("Search error:", error);
    } finally {
      setIsSearchLoading(false);
    }
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      debouncedSearch(searchQuery);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, debouncedSearch]);

  // Handle schaden selection
  const handleSchadenSelect = (event, schaden) => {
    if (schaden) {
      navigate(`/schaden/${schaden.id}`);
    }
  };

  const getStatusLabel = (status) => {
    if (!status) {
      return "";
    }

    if (typeof status === "string") {
      return status;
    }

    if (typeof status === "object") {
      if (status.label) {
        return status.label;
      }

      if (status.value) {
        return status.value;
      }
    }

    return "";
  };

  const getStatusValue = (status) => {
    if (!status) {
      return "";
    }

    if (typeof status === "string") {
      return status;
    }

    if (typeof status === "object") {
      if (status.value) {
        return status.value;
      }

      if (status.label) {
        return status.label;
      }
    }

    return "";
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 4,
          alignItems: "stretch",
          width: "100%",
          ...sx,
        }}
      >
        {/* Search autocomplete */}
        <Autocomplete
          sx={{
            flex: 1,
            width: "100%",
            height: "3.5rem",
          }}
          freeSolo={false}
          filterOptions={(options) => options}
          options={searchResults}
          loading={isSearchLoading}
          noOptionsText={
            searchQuery.length < 2
              ? "Mindestens 2 Zeichen eingeben..."
              : "Keine Schäden gefunden"
          }
          getOptionLabel={(schaden) => schaden.schadennummer || ""}
          renderOption={(props, schaden) => {
            const { key, ...restProps } = props;
            return (
              <Box component="li" {...restProps}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">
                      <span
                        style={{ fontWeight: typographyTokens.weight.semibold }}
                      >
                        {schaden.schaden_nummer_accredo}
                      </span>
                      {schaden.schaden_nummer_kd && (
                        <span style={{}}> | {schaden.schaden_nummer_kd}</span>
                      )}
                      {schaden.schaden_nummer_vu && (
                        <span style={{}}> | {schaden.schaden_nummer_vu}</span>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {schaden?.objekt?.bezeichnung ||
                        `${schaden.objekt?.strasse || ""} ${schaden.objekt?.hausnr || ""}`.trim() ||
                        "Objekt"}
                    </Typography>
                  </Box>
                  {getStatusLabel(schaden.status) && (
                    <ClaimStatusBadge
                      status={getStatusValue(schaden.status)}
                      label={getStatusLabel(schaden.status)}
                      size="tiny"
                    />
                  )}
                </Box>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Schaden suchen..."
              variant="outlined"
              size="medium"
              sx={{
                flex: 1,
                width: "100%",
                height: "100%",
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <SearchOutlined sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                  endAdornment: (
                    <>
                      {isSearchLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  sx: {
                    backgroundColor: "background.paper",
                    borderRadius: 2,
                    py: "36px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                  },
                },
              }}
            />
          )}
          onChange={handleSchadenSelect}
          onInputChange={(event, value) => setSearchQuery(value)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />

        <Button
          variant="primary"
          size="large"
          onClick={() => setModalOpen(true)}
          startIcon={<NewReleasesOutlined />}
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          Neuen Schaden anlegen
        </Button>
      </Box>
      {/* Modal for creating new Schaden */}
      <NewDamageClaimDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}

ClaimsSearchBar.propTypes = {
  sx: PropTypes.object,
};
